import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { User } from '../classes/user';
import { MatSnackBar } from '@angular/material/snack-bar';
import {ToastrService} from 'ngx-toastr';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';


export enum UserSaveError { unknown, wrongPassword, emailAlreadyUsed, emailInvalid }
export enum UserUpdateError { unknown, emailAlreadyUsed, emailInvalid }


@Injectable( { providedIn: 'root' } )
export class UserService {


	usernameCache: { [key:string] : string } = {};
	public twoFASetupComplete: Subject<boolean> = new Subject<boolean>();


	constructor(
		@Inject('environment') private environment: any,
		private httpClient: HttpClient,
		private matSnackBar: MatSnackBar,
		private toastr: ToastrService,
		private router: Router,
	) {}


	createUser( user: User ): Observable<User | UserSaveError> {

		return this.httpClient.post<{ benutzer: User, verarbeitet: boolean, meldung?: string  }>(
			this.environment.backendUrl + 'benutzer/new',
			user,
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					const errorMsg = response.meldung || 'Benutzererstellung fehlgeschlagen';
					console.log( "User creation failed", response );
					this.matSnackBar.open( errorMsg, '', { duration: 5000 } );
					return errorMsg;
				}

				if( !response.verarbeitet ) {

					if( response.meldung == "Diese E-Mail-Adresse wurde schon benutzt" ) {
						return UserSaveError.emailAlreadyUsed;
					}
					if( response.meldung?.includes( "Das Kennwort entspricht nicht den Richtlinien" ) ) {
						return UserSaveError.wrongPassword;
					}
					
					console.log( "User creation failed", response );
					this.matSnackBar.open( 'Benutzererstellung fehlgeschlagen', '', { duration: 5000 } );
					return UserSaveError.unknown;

				}

				this.matSnackBar.open( 'Benutzererstellung erfolgreich', '', { duration: 5000 } );
				return response.benutzer;
			}),
			catchError((err) => {
				if (err.status === 406) return of(UserSaveError.wrongPassword);
				const errorMsg = err.error?.meldung || 'Beim Erstellen des Benutzers ist ein Fehler aufgetreten';

				if (err.status === 500) {
					if (err.error?.meldung.includes("Das Kennwort entspricht nicht den Richtlinien")) {
						return of(UserSaveError.wrongPassword);
					}
					if (err.error?.meldung.includes("Diese E-Mail-Adresse wurde schon benutzt")) {
						return of(UserSaveError.emailAlreadyUsed);
					}
					if (err.error?.meldung.includes("Ungültige Email")) {
						return of(UserSaveError.emailInvalid);
					}
				}

				console.log("An error occurred while creating the user", err);
				this.matSnackBar.open(errorMsg, '', { duration: 5000 });
				return of(errorMsg);
			})
		);

	}

	updateUser(user: User): Observable<User> {
		return this.httpClient.post<{ benutzer: User, verarbeitet: boolean, meldung?: string }>(
			this.environment.backendUrl + 'benutzer/update',
			user,
		).pipe(
			mergeMap(response => {
				if (!response || !response.verarbeitet) {
					const errorMsg = response.meldung || 'Benutzerbearbeitung fehlgeschlagen';
					console.log("User edit failed", response);
					this.matSnackBar.open(errorMsg, '', { duration: 5000 });
	
					return throwError(() => new Error(errorMsg));
				}
	
				this.matSnackBar.open('Benutzerbearbeitung erfolgreich', '', { duration: 5000 });
				return of(response.benutzer);
			}),
			catchError(err => {
				console.log("An error occurred while editing the user", err);
				const errorMsg = err.error?.meldung || 'Beim Bearbeiten des Benutzers ist ein Fehler aufgetreten';
				this.matSnackBar.open(errorMsg, '', { duration: 5000 });
	
				return throwError(() => new Error(errorMsg));
			})
		);
	}

	updateUserAddress( user: User ): Observable<User|UserUpdateError> {

		return this.httpClient.put<{ benutzer: User, verarbeitet: boolean }>(
			this.environment.backendUrl + 'benutzer/update/anshrift',
			user,
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet || !response.benutzer ) {
					console.log( "Useraddress edit failed", response );
					this.matSnackBar.open( 'Anschrifttbearbeitung fehlgeschlagen', '', { duration: 5000 } );
					return UserUpdateError.unknown;
				}

				this.matSnackBar.open( 'Anschrifttbearbeitung erfolgreich', '', { duration: 5000 } );
				return response.benutzer;
				
			}),
			catchError( err => {

				if( err && err.status == 500 ) {

					if( err.error?.meldung.includes( "Diese E-Mail-Adresse wurde schon benutzt" ) ) {
						return of( UserUpdateError.emailAlreadyUsed );
					}
					if( err.error?.meldung.includes( "Ungültige Email" ) ) {
						return of( UserUpdateError.emailInvalid );
					}
					
				}

				console.log( "An error occurred while editing the user", err );
				this.matSnackBar.open( 'Beim Bearbeiten des Benutzers ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(UserUpdateError.unknown);

			})
		);

	}

	getAll( bIncludeDeleted = false ): Observable<User[]> {

		const hParams = bIncludeDeleted
			? new HttpParams().append( "includeDeleted", "true" )
			: new HttpParams();

		return this.httpClient.get<User[]>(
			this.environment.backendUrl + 'benutzer/all',
			{ params: hParams }
		).pipe(
			map( users => {
				if( !users ) users = [];
				for( const u of users ) {
					if( !u.ebeId ) u.ebeId = '';
				}
				users.sort( (a,b) => a.ebeId.localeCompare(b.ebeId) );
				return users;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of users', err );
				this.matSnackBar.open( 'Beim Abrufen der Benutzer-Liste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return [];
			})
		);

	}

	set2FactorAuthSettings( userId: string, authenticatorActive: boolean, emailActive: boolean, zweiFaktorToken: string ): Observable<User | null> {

		console.warn('show UserID in set2FactorAuthSettings', userId);

		const body = {
			ebeId: userId,
			zweiFaktorToken: zweiFaktorToken
		  };
		

		return this.httpClient.put<{ verarbeitet: boolean, benutzer: User }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/status',
			body,
			{
				params: new HttpParams()
					.set( 'ebeId', userId )
					.set( 'appActive', authenticatorActive ? "true" : "false" )
					.set( 'emailActive', emailActive ? "true" : "false" )
			}
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( "Änderung abgelehnt", '', { duration: 5000 } );
					return null;
				}
                this.twoFASetupComplete.next(true); 

				return response.benutzer;
			}),
			catchError( err => {
				this.matSnackBar.open( 'beim Ändern der Einstellungen ist ein Fehler aufgetreten', '', { duration: 5000 } );
				console.log( 'an error occurred while changing the settings', err );
				return of(null);
			})
		);
	}


	set2FactorAuthSettingsUsermanagement( userId: string, authenticatorActive: boolean, emailActive: boolean, zweiFaktorToken: string ): Observable<User | null> {

		console.warn('show UserID in set2FactorAuthSettings', userId);

		const body = {
			ebeId: userId,
			zweiFaktorToken: zweiFaktorToken
		  };
		

		return this.httpClient.put<{ verarbeitet: boolean, benutzer: User }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/status/benutzerverwaltung',
			body,
			{
				params: new HttpParams()
					.set( 'ebeId', userId )
					.set( 'appActive', authenticatorActive ? "true" : "false" )
					.set( 'emailActive', emailActive ? "true" : "false" )
			}
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( "Änderung abgelehnt", '', { duration: 5000 } );
					return null;
				}
                this.twoFASetupComplete.next(true); 

				return response.benutzer;
			}),
			catchError( err => {
				this.matSnackBar.open( 'beim Ändern der Einstellungen ist ein Fehler aufgetreten', '', { duration: 5000 } );
				console.log( 'an error occurred while changing the settings', err );
				return of(null);
			})
		);
	}

	validateF2A( userId: string, zweiFaktorToken: string, totPCode: string ): Observable<boolean> {

		console.log(userId, 'USER ID');

		return this.httpClient.post<{ benutzer: User, verarbeitet: boolean }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/validate/first',
			{ ebeId: userId, zweiFaktorToken: zweiFaktorToken,  zweiFaktorId: totPCode },
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					console.log( "2fa failed", response );
					this.matSnackBar.open( '2fa Benutzerbearbeitung fehlgeschlagen', '', { duration: 5000 } );
					return false;
				}

				this.matSnackBar.open( '2fa Benutzerbearbeitung erfolgreich', '', { duration: 5000 } );
				this.router.navigate(['/auth/login']);
				return response.verarbeitet;
			}),
			catchError(err => {
				console.log( "An error occurred while editing the user", err );
				this.matSnackBar.open( 'Beim Bearbeiten des Benutzers ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(false);
			})
		);

	}

	validateF2AUsermanagement( userId: string, totPCode: string ): Observable<boolean> {

		return this.httpClient.post<{ benutzer: User, verarbeitet: boolean }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/validate/first/benutzerverwaltung',
			{ ebeId: userId, zweiFaktorId: totPCode },
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {
					console.log( "2fa failed", response );
					this.matSnackBar.open( '2fa Benutzerbearbeitung fehlgeschlagen', '', { duration: 5000 } );
					return false;
				}

				this.matSnackBar.open( '2fa Benutzerbearbeitung erfolgreich', '', { duration: 5000 } );
				return response.verarbeitet;
			}),
			catchError(err => {
				console.log( "An error occurred while editing the user", err );
				this.matSnackBar.open( 'Beim Bearbeiten des Benutzers ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(false);
			})
		);

	}

  deleteUser( userId: string ): Observable<boolean> {
    return this.httpClient.post(this.environment.backendUrl + 'benutzer/delete', userId).pipe(
      map(() => {
          this.toastr.success('Benutzer wurde erfolgreich gelöscht');
          return true;
        }
      ),
      catchError(err => {
        this.toastr.error('Beim Löschen des Benutzers ist ein Fehler aufgetreten');
        return of(false);
      })
    );
  }


  	resetLoginAttempts( userId: string ): Observable<boolean> {

		return this.httpClient.put<{ verarbeitet: boolean }>(
			this.environment.backendUrl + 'benutzer/login/reset?ebeId=' + userId,
			{}
		).pipe(
			catchError( error => {
				return of({ verarbeitet: false });
			}),
			map( response => {

				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Beim Zurücksetzen der Anmeldeversuche ist ein Fehler aufgetreten', 'Fehler' );
					return false;
				}
				else {
					this.matSnackBar.open( 'Anmeldeversuche zurückgesetzt', 'Erfolg' );
					return true;
				}

			}),
		);

	}



	loadUsernameCache(): Observable<{ [key:string]: string }> {

		return this.getAll( true ).pipe(
			map( users => {
				this.usernameCache = {};
				for( let user of users ) {
					const arr: string[] = [];
					if( user.ebeVorname ) arr.push( user.ebeVorname );
					if( user.ebeName ) arr.push( user.ebeName );
					this.usernameCache[ user.ebeId ] = ( arr.join( " " ) || user.ebeId );
				}
				return structuredClone( this.usernameCache );
			})
		);

	}

	loadMitgliedsnamenCache(): Observable<{ [key: string]: string }> {

		return this.getAll( true ).pipe(
			map(users => {
				const mitgliedsnamenCache: { [key: string]: string } = {};
				users.forEach(user => {
					mitgliedsnamenCache[user.ebeId] = user.mitgliedsname as string || user.ebeId;
				});
				return mitgliedsnamenCache;
			})
		);
	}

	getCachedUsernames( userIds: string[] ): string[] {

		const usernames: string[] = [];
		for( let userId of userIds ) {
			usernames.push( this.usernameCache[ userId ] );
		}
		usernames.sort( (a,b) => a.localeCompare( b ) );
		return usernames;

	}

  updateGutBeraten(user: User): Observable<User> {
   return this.httpClient.put<User>(this.environment.backendUrl + 'benutzer/gutberaten', user).pipe(
      map((response) => {
        this.matSnackBar.open('Benutzer wurde erfolgreich aktualisiert', '', {duration: 5000});
        return response;
      }
      ),
      catchError(err => {
        this.matSnackBar.open('Beim Aktualisieren des Benutzers ist ein Fehler aufgetreten', '', {duration: 5000});
        return throwError(err);
      }
      )
    );
  }



}
